import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { Home, AlertTriangle, ClipboardList, FileText, BarChart3, Users, Bell, CreditCard, LogOut, Settings, Sparkles, Building2 } from 'lucide-react';

function AppShell({ children, permissions }) {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, organization, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: Home, svgIcon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/deviations', label: t('nav.cases'), icon: AlertTriangle, svgIcon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
    { path: '/tasks', label: t('nav.tasks'), icon: ClipboardList, svgIcon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
    { path: '/reports', label: t('nav.reports'), icon: FileText, svgIcon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { path: '/analytics', label: t('nav.analytics'), icon: BarChart3, svgIcon: 'M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { path: '/gap-analysis', label: t('nav.gapAnalysis'), icon: Sparkles, svgIcon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z' },
  ];

  if (permissions?.is_admin || permissions?.is_power_user) {
    navItems.push({ 
      path: '/users', 
      label: t('nav.users'), 
      icon: Users,
      svgIcon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' 
    });
  }

  // Bottom nav: max 5 items for mobile
  const bottomNavItems = navItems.slice(0, 5);

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-7 md:h-8"
              />
              <span className="font-semibold text-white hidden sm:block text-sm md:text-base">
                {organization?.name || ''}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? 'bg-cyan-500/10 text-cyan-400'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex items-center gap-2 md:gap-3">
              <LanguageSwitcher />
              
              {/* User dropdown (desktop) */}
              <div className="relative group hidden md:block">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-800/50 transition-colors">
                  <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-slate-300">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <span className="text-sm text-slate-300 hidden lg:block">{user?.name || user?.email}</span>
                </button>
                
                <div className="absolute right-0 top-full mt-1 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="p-2">
                    <Link to="/settings/company" className="flex items-center gap-3 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700/50 rounded-lg" data-testid="company-profile-link">
                      <Building2 className="w-4 h-4 text-amber-400" />
                      <span>{t('nav.companyProfile')}</span>
                    </Link>
                    <Link to="/settings/notifications" className="flex items-center gap-3 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700/50 rounded-lg" data-testid="notifications-settings-link">
                      <Bell className="w-4 h-4 text-cyan-400" />
                      <span>{t('nav.notifications')}</span>
                    </Link>
                    <Link to="/subscription" className="flex items-center gap-3 px-4 py-3 text-sm text-slate-200 hover:bg-slate-700/50 rounded-lg">
                      <CreditCard className="w-4 h-4 text-emerald-400" />
                      <span>{t('nav.subscription')}</span>
                    </Link>
                    <div className="my-2 border-t border-slate-700" />
                    <button onClick={logout} className="flex items-center gap-3 w-full px-4 py-3 text-sm text-slate-200 hover:bg-rose-500/10 hover:text-rose-400 rounded-lg" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile: Settings gear */}
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-slate-400 hover:text-white"
                data-testid="mobile-menu-toggle"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile settings dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 px-4 py-3 space-y-1">
            <div className="px-3 py-2 text-sm text-slate-500 border-b border-slate-800 mb-2">
              {user?.name || user?.email}
            </div>
            <Link to="/settings/company" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm text-slate-300 hover:bg-slate-800 rounded-lg">
              <Building2 className="w-4 h-4 text-amber-400" />
              {t('nav.companyProfile')}
            </Link>
            <Link to="/settings/notifications" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm text-slate-300 hover:bg-slate-800 rounded-lg">
              <Bell className="w-4 h-4 text-cyan-400" />
              {t('nav.notifications')}
            </Link>
            <Link to="/subscription" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm text-slate-300 hover:bg-slate-800 rounded-lg">
              <CreditCard className="w-4 h-4 text-emerald-400" />
              {t('nav.subscription')}
            </Link>
            {navItems.length > 5 && navItems.slice(5).map(item => (
              <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-3 py-3 text-sm text-slate-300 hover:bg-slate-800 rounded-lg">
                <item.icon className="w-4 h-4 text-slate-400" />
                {item.label}
              </Link>
            ))}
            <button onClick={() => { setMobileMenuOpen(false); logout(); }} className="flex items-center gap-3 w-full px-3 py-3 text-sm text-rose-400 hover:bg-rose-500/10 rounded-lg">
              <LogOut className="w-4 h-4" />
              {t('nav.logout')}
            </button>
          </div>
        )}
      </header>

      {/* Main Content - extra bottom padding for mobile bottom nav */}
      <main className="pt-14 md:pt-16 pb-20 md:pb-0 min-h-screen">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-xl border-t border-slate-800/60 md:hidden" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-1 safe-bottom">
          {bottomNavItems.map(item => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-1 rounded-lg transition-all ${
                  active ? 'text-cyan-400' : 'text-slate-500 active:text-slate-300'
                }`}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-cyan-400' : ''}`} />
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default AppShell;
